// Chakra Healing Frome - Service Page
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, MapPin } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const ChakraHealingFrome = () => {
  usePageMeta({
    title: "Chakra Healing in Frome | Energy Balancing Somerset",
    description: "Chakra healing and balancing in Frome, Somerset. Work with your body's energy centres to release blocks, restore harmony, and support emotional and",
    canonical: "https://annelielily.com/treatments/chakra-healing"
  });

  const serviceSchema = generateServiceSchema({
    name: "Chakra Healing in Frome",
    description: "Chakra balancing and healing sessions to work with the body's energy centres, releasing blocks and restoring harmony.",
    serviceType: "Chakra Healing Treatment",
    url: "https://annelielily.com/treatments/chakra-healing",
    image: "https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/85zwhbzx_IMG_3954.jpeg"
  });

  const faqs = [
    {
      question: "What are chakras?",
      answer: "Chakras are energy centres in the body, traditionally described in Indian spiritual traditions. There are seven main chakras running from the base of your spine to the crown of your head, each associated with different aspects of physical, emotional, and spiritual wellbeing."
    },
    {
      question: "How do you know if your chakras are out of balance?",
      answer: "Imbalances can show up as physical symptoms, emotional patterns, or a general sense of being 'stuck'. For example, a blocked throat chakra might manifest as difficulty expressing yourself, while an imbalanced root chakra might show as anxiety or insecurity."
    },
    {
      question: "What happens during a chakra healing session?",
      answer: "Sessions typically involve a combination of energy work, sometimes with crystals placed on or near the body. You'll lie comfortably while I work with each chakra, sensing and clearing any blocks. It's deeply relaxing."
    },
    {
      question: "Is chakra healing the same as Reiki?",
      answer: "They're related but different. Reiki is a specific energy healing technique, while chakra healing focuses specifically on the chakra system. I often incorporate both in treatments, depending on what's needed."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  const chakras = [
    { name: "Root (Muladhara)", color: "red", focus: "Security, grounding, basic needs" },
    { name: "Sacral (Svadhisthana)", color: "orange", focus: "Creativity, emotions, sexuality" },
    { name: "Solar Plexus (Manipura)", color: "yellow", focus: "Personal power, confidence, will" },
    { name: "Heart (Anahata)", color: "green", focus: "Love, compassion, connection" },
    { name: "Throat (Vishuddha)", color: "blue", focus: "Communication, expression, truth" },
    { name: "Third Eye (Ajna)", color: "indigo", focus: "Intuition, wisdom, insight" },
    { name: "Crown (Sahasrara)", color: "violet", focus: "Spiritual connection, consciousness" },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Chakra Healing" currentSlug="treatments/chakra-healing" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
                Chakra Healing in Frome
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
                Chakra Healing
              </h1>
              <p className="text-neutral-700 leading-relaxed mb-6">
                Working with the body's energy centres to release what's stuck and restore 
                natural balance. If you're drawn to energy work and want to understand 
                yourself on a deeper level, chakra healing offers a structured way in.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary">
                  Book a Session <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-neutral-600">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold" />
                  60-90 minutes
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  Frome, Somerset
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/85zwhbzx_IMG_3954.jpeg"
                  alt="Annelie Lily using a crystal pendulum for chakra healing and energy balancing with a client"
                  className="w-full h-full object-cover"
                  width={600}
                  height={600}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* The Chakras */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              The Seven Main Chakras
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {chakras.map((chakra, index) => (
                <div key={index} className="bg-white/70 p-4 rounded-xl border border-gold/10">
                  <h3 className="font-heading text-lg text-neutral-800 mb-1">{chakra.name}</h3>
                  <p className="text-neutral-600 text-sm">{chakra.focus}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* My Approach */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              My Approach to Chakra Work
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                My fascination with chakras began during my <Link to="/yoga-classes" className="text-gold hover:underline">yoga teacher training</Link> with the British 
                Wheel of Yoga. Learning about these energy centres opened up a whole new dimension 
                to the practice — and led me to train in <Link to="/treatments/reiki-healing" className="text-gold hover:underline">Reiki</Link>, crystal healing, and eventually 
                become an AuraMediator.
              </p>
              <p>
                I believe that when all the chakras are in balance, the body and mind naturally 
                move towards health. Most imbalances — whether physical, emotional, or mental — 
                have roots in the energy body. Chakra healing addresses these roots.
              </p>
              <p>
                Sessions might include energy sensing and clearing, crystal placement, guided 
                visualization, and Reiki. What we do depends on what comes up — each session is 
                different because each person is different.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Questions About Chakra Healing
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                  <p className="text-neutral-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Interested in Chakra Work?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Get in touch to discuss whether chakra healing might help with what you're 
              experiencing. I'm happy to explain more about how it works.
            </p>
            <Link to="/contact" className="btn-primary">
              Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <h3 className="font-heading text-xl text-neutral-800 mb-6 text-center">Related Treatments</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/treatments/reiki-healing" className="text-sm text-gold hover:underline underline-offset-4">Reiki Healing</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments/swedish-massage" className="text-sm text-gold hover:underline underline-offset-4">Swedish Massage</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments" className="text-sm text-gold hover:underline underline-offset-4">All Treatments</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/yin-yoga" className="text-sm text-gold hover:underline underline-offset-4">Yin Yoga</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChakraHealingFrome;
